import { DatePipe } from '@angular/common';
import {
  isLegacyQualityRule,
  isLogisticsV2Version,
  sanitizeV2RuleForApi,
  shouldEmitQualitySlabCondition,
} from './quality-conditions.model';
import { buildMaxDayVsSlotsPolicy } from './payout-policy.model';
import { ASSIGNMENT_POLICY_TYPE_MICRO_CORRIDOR } from './mc-incentive.model';
import { isDailyTimingSetConfigured } from './daily-set-config';

export interface BuildIncentivePayloadOptions {
  active: boolean;
  state: string;
  duplicateState: string;
  editState: string;
  datePipe: DatePipe;
  generateUuid: () => string;
  /** Logged-in user email (create-slab parity); server may also inject from JWT. */
  actorEmail?: string;
  /** Preserves immutable MaxDayVsSlots policy when edit flow does not re-emit selection. */
  existingPayoutPolicy?: any;
  /**
   * On started-incentive edit, SMS fields are locked in UI; payload must match GET
   * so captain-incentive allowlist (endDate/active only) is not tripped by form drift.
   */
  readonlySmsFromGet?: {
    smsTemplate: string;
    messageLocale: string;
  };
}

function applyActorAuditFields(incentiveObj: any, options: BuildIncentivePayloadOptions): void {
  const email = options.actorEmail != null ? String(options.actorEmail).trim() : '';
  if (!email) {
    return;
  }
  if (options.state === options.editState) {
    incentiveObj.updatedBy = email;
    return;
  }
  incentiveObj.createdBy = email;
}

function finalizeIncentivePayload(
  incentiveObj: any,
  options: BuildIncentivePayloadOptions,
): any {
  applyActorAuditFields(incentiveObj, options);
  return incentiveObj;
}

function makeIncentiveOrdersAndDistanceCumulative(incentiveData: any): any {
  for (const index in incentiveData) {
    if (index) {
      const keys = Object.keys(incentiveData[index]);
      for (const key in keys) {
        if (keys[key]) {
          let distanceSum = 0;
          let orderSum = 0;
          for (const rule in incentiveData[index][keys[key]].rules) {
            if (rule) {
              if (incentiveData[index][keys[key]].rules[rule].order) {
                orderSum += incentiveData[index][keys[key]].rules[rule].order;
                incentiveData[index][keys[key]].rules[rule].order = orderSum;
              }
              if (incentiveData[index][keys[key]].rules[rule].distance) {
                distanceSum += incentiveData[index][keys[key]].rules[rule].distance;
                incentiveData[index][keys[key]].rules[rule].distance = distanceSum;
              }
            }
          }
        }
      }
    }
  }
  return incentiveData;
}

function orderGoal(goals: any): void {
  Object.keys(goals).forEach(day => {
    const dayGoal = goals[day];
    dayGoal.forEach((goal: any) => {
      goal.rules = goal.rules || [];
      goal.rules = goal.rules.sort((g1: any, g2: any) => {
        if (g1.order === g2.order) {
          return g1.distance > g2.distance ? 1 : -1;
        }
        return g1.order > g2.order ? 1 : -1;
      });
      goal.rules.map((rule: any, index: number) => {
        rule.index = index + 1;
      });
    });
  });
}

function sanitizeWeeklyRuleForApi(rule: any): any {
  const sanitized = { ...rule };
  delete sanitized.qualityConditions;
  if (sanitized.quality && !isLegacyQualityRule(sanitized)) {
    delete sanitized.quality;
  }
  return sanitized;
}

function applyQualitySlabCondition(incentiveObj: any, goalsInfo: any): void {
  const value = goalsInfo && goalsInfo.qualitySlabCondition;
  if (shouldEmitQualitySlabCondition(value)) {
    incentiveObj.qualitySlabCondition = value;
  }
  if (!value && incentiveObj.selectedVariable) {
    const qualityIndex = incentiveObj.selectedVariable.indexOf('quality');
    if (qualityIndex !== -1) {
      incentiveObj.selectedVariable.splice(qualityIndex, 1);
    }
  }
}

export function buildIncentiveApiPayload(
  incentiveData: any,
  options: BuildIncentivePayloadOptions,
): any {
  const {
    active,
    state,
    duplicateState,
    editState,
    datePipe,
    generateUuid,
    existingPayoutPolicy,
  } = options;
  const incentiveObj: any = {};
  incentiveObj.type = 'incentives';
  incentiveObj.incentiveName = incentiveData.basicInfo.incentiveName;
  incentiveObj.incentiveType = incentiveData.basicInfo.incentiveType;
  incentiveObj.cities = incentiveData.basicInfo.city;
  incentiveObj.shift = incentiveData.basicInfo.shift;
  incentiveObj.serviceType = incentiveData.basicInfo.service;
  incentiveObj.serviceNames = incentiveData.basicInfo.serviceNames;
  incentiveObj.ruleName = incentiveData.basicInfo.ruleName;
  incentiveObj.ruleId = incentiveData.basicInfo.ruleId;
  incentiveObj.startDate = incentiveData.basicInfo.startDate;
  incentiveObj.endDate = incentiveData.basicInfo.endDate;
  incentiveObj.priority = incentiveData.basicInfo.priority;
  const communication = incentiveData.communicationInfo || {};
  incentiveObj.smsTemplate = communication.smsTemplate;
  incentiveObj.messageLocale = communication.messageLocale;
  incentiveObj.tnc = communication.termsAndCondition;
  if (options.readonlySmsFromGet) {
    incentiveObj.smsTemplate = options.readonlySmsFromGet.smsTemplate;
    incentiveObj.messageLocale = options.readonlySmsFromGet.messageLocale;
  }
  incentiveObj.active = active;

  if (isLogisticsV2Version(incentiveData.basicInfo.incentiveVersion)) {
    incentiveObj.incentiveVersion = 'v2';
    incentiveObj.payoutType = incentiveData.basicInfo.payoutType;
  }

  if (incentiveData.basicInfo.isHHIncentive) {
    incentiveObj.tags = ['HH'];
  }

  if (incentiveData.basicInfo.isMCIncentive && incentiveData.basicInfo.microCorridorId) {
    const mcName = incentiveData.basicInfo.microCorridorName;
    incentiveObj.assignmentPolicy = {
      type: ASSIGNMENT_POLICY_TYPE_MICRO_CORRIDOR,
      ids: [String(incentiveData.basicInfo.microCorridorId)],
      ...(mcName != null && String(mcName).trim() !== ''
        ? { name: String(mcName).trim() }
        : {}),
    };
    incentiveObj.ruleName = '';
    incentiveObj.ruleId = '';
  }

  const goalsInfo = incentiveData.goalsInfo;
  if (!goalsInfo) {
    return finalizeIncentivePayload(incentiveObj, options);
  }

  if (incentiveData.basicInfo.incentiveType === 'Redeem') {
    incentiveObj.redeemCondition = goalsInfo.selectedCondition;
    incentiveObj.redeem = goalsInfo.redeem;
    return finalizeIncentivePayload(incentiveObj, options);
  }

  if (incentiveData.basicInfo.incentiveType === 'Daily' || incentiveData.basicInfo.incentiveType === 'Adhoc') {
    if (incentiveData.basicInfo.incentiveType === 'Daily') {
      if (goalsInfo.cancellationChecked) {
        incentiveObj.cancellationLimit = goalsInfo.cancellationLimit;
        if (goalsInfo.selectedVariable.indexOf('cancellation') === -1) {
          goalsInfo.selectedVariable.push('cancellation');
        }
      }
    } else if (incentiveData.basicInfo.incentiveType === 'Adhoc') {
      if (goalsInfo.maxOrderDistanceChecked) {
        incentiveObj.maxOrderDistance = goalsInfo.maxOrderDistanceGlobal;
        if (goalsInfo.selectedVariable.indexOf('maxOrderDistance') === -1) {
          goalsInfo.selectedVariable.push('maxOrderDistance');
        }
      }
      incentiveObj.incentiveDisplayName = incentiveData.basicInfo.incentiveDisplayName;
    }
    incentiveObj.selectedCondition = (goalsInfo.selectedCondition instanceof Array)
      ? '' : goalsInfo.selectedCondition;
    incentiveObj.selectedVariable = goalsInfo.selectedVariable;
    if (incentiveData.basicInfo.incentiveType === 'Daily'
      && isLogisticsV2Version(incentiveData.basicInfo.incentiveVersion)) {
      applyQualitySlabCondition(incentiveObj, goalsInfo);
    }

    const dailyIncentiveData = makeIncentiveOrdersAndDistanceCumulative(
      JSON.parse(JSON.stringify(goalsInfo.dailyIncentiveData)),
    );
    const goals: any = {};
    goals['Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday'] = [];
    const uuidBySetKey: Record<string, string> = {};
    for (const index in dailyIncentiveData) {
      if (index) {
        const setKeys = Object.keys(dailyIncentiveData[index])
          .filter(k => k !== 'days')
          .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
        for (let keyIdx = 0; keyIdx < setKeys.length; keyIdx++) {
          const setKey = setKeys[keyIdx];
          if (setKey) {
            if (!isDailyTimingSetConfigured(dailyIncentiveData[index][setKey])) {
              continue;
            }
            const existingUuid = dailyIncentiveData[index][setKey].uuid;
            const newSet: any = {
              timeSlot: [],
              rules: [],
              uuid: generateUuid(),
            };
            const setIndex = parseInt(setKey, 10) - 1;
            if (goalsInfo.cancellationChecked) {
              newSet.cancellationLimit = goalsInfo.setsCancellationLimit[setIndex];
            }
            if (isLogisticsV2Version(incentiveData.basicInfo.incentiveVersion)) {
              const slotTitle = dailyIncentiveData[index][setKey].slotTitle;
              if (slotTitle != null && String(slotTitle).trim() !== '') {
                newSet.slotTitle = String(slotTitle).trim();
              }
            }
            if (state === editState && existingUuid) {
              newSet.uuid = existingUuid;
            } else if (state === duplicateState) {
              newSet.uuid = generateUuid();
            } else {
              newSet.uuid = existingUuid || generateUuid();
            }
            uuidBySetKey[setKey] = newSet.uuid;
            newSet.timeSlot = dailyIncentiveData[index][setKey].timeSlot;
            for (const i in newSet.timeSlot) {
              if (newSet.timeSlot[i]) {
                try {
                  newSet.timeSlot[i].fromTime = datePipe.transform(newSet.timeSlot[i].fromTime, 'HH:mm');
                  newSet.timeSlot[i].toTime = datePipe.transform(newSet.timeSlot[i].toTime, 'HH:mm');
                } catch (e) {
                  console.log(e);
                }
              }
            }
            newSet.rules = dailyIncentiveData[index][setKey].rules.map((rule: any) => {
              if (incentiveData.basicInfo.incentiveType === 'Daily'
                && isLogisticsV2Version(incentiveData.basicInfo.incentiveVersion)) {
                return sanitizeV2RuleForApi(rule);
              }
              return rule;
            });
            if (goalsInfo.cancellationChecked) {
              for (const rule in newSet.rules) {
                if (goalsInfo.cancellationLimitScope === 'global') {
                  newSet.rules[rule].cancellationLimit = goalsInfo.cancellationLimit;
                } else {
                  newSet.rules[rule].cancellationLimit = goalsInfo.setsCancellationLimit[setIndex];
                }
              }
            } else {
              for (const rule in newSet.rules) {
                if (newSet.rules[rule].cancellationLimit != null) {
                  delete newSet.rules[rule].cancellationLimit;
                }
              }
            }
            goals['Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday'].push(newSet);
          }
        }
      }
    }
    orderGoal(goals);
    incentiveObj.goals = goals;
    if (incentiveData.basicInfo.incentiveType === 'Daily'
      && isLogisticsV2Version(incentiveData.basicInfo.incentiveVersion)
      && incentiveData.basicInfo.payoutType === 'Deferred') {
      if (goalsInfo.deferredPayoutPolicy) {
        const payoutPolicy = buildMaxDayVsSlotsPolicy(goalsInfo.deferredPayoutPolicy, uuidBySetKey);
        if (payoutPolicy) {
          incentiveObj.payoutPolicy = payoutPolicy;
        }
      } else if (state === editState && existingPayoutPolicy) {
        incentiveObj.payoutPolicy = existingPayoutPolicy;
      }
    }
    return finalizeIncentivePayload(incentiveObj, options);
  }

  incentiveObj.selectedCondition = goalsInfo.selectedCondition;
  incentiveObj.selectedVariable = goalsInfo.selectedVariable;

  const weeklyData = makeIncentiveOrdersAndDistanceCumulative(
    JSON.parse(JSON.stringify(goalsInfo.weeklyIncentiveData)),
  );
  const goals: any = {};
  for (const goal in weeklyData) {
    if (goal && weeklyData[goal]) {
      const day = weeklyData[goal].days;
      delete weeklyData[goal].days;
      delete weeklyData[goal].startDay;
      delete weeklyData[goal].endDay;
      delete weeklyData[goal].disabled;
      goals[day] = [];
      for (const rule in weeklyData[goal]) {
        if (rule && weeklyData[goal][rule]) {
          for (const time in weeklyData[goal][rule].timeSlot) {
            if (time && weeklyData[goal][rule].timeSlot[time]) {
              weeklyData[goal][rule].timeSlot[time].fromTime =
                datePipe.transform(weeklyData[goal][rule].timeSlot[time].fromTime, 'HH:mm');
              weeklyData[goal][rule].timeSlot[time].toTime =
                datePipe.transform(weeklyData[goal][rule].timeSlot[time].toTime, 'HH:mm');
            }
          }
          if (state === duplicateState) {
            weeklyData[goal][rule].uuid = generateUuid();
          }
          const goalSet = weeklyData[goal][rule];
          if (goalSet.rules) {
            goalSet.rules = goalSet.rules.map((r: any) => sanitizeWeeklyRuleForApi(r));
          }
          goals[day].push(goalSet);
        }
      }
    }
  }
  orderGoal(goals);
  incentiveObj.goals = goals;
  return finalizeIncentivePayload(incentiveObj, options);
}
